import {Injectable, OnDestroy} from '@angular/core';
import {GtWindowService} from '@app/services/gt-window.service';
import {environment} from '@environment';
import {
  Absent,
  AgentStateTransition,
  AnsweredCall,
  Available,
  CallInfo,
  CallLogEvent,
  CallRetrieved,
  CallToggled,
  CreateCall,
  Dequeue,
  DisconnectedCall,
  Enqueue,
  EUAnsweredCall,
  EUCreateCall,
  EUDisconnectedCall,
  EUExpiredCall,
  EUNoConnectCall,
  MobilityManagementUpdateEvent,
  NoConnectCall,
  SkillAdded,
  SkillChanged,
  SkillRemoved,
  TransferCallAnswered,
  TransferCallEnd,
  TransferCallEvent
} from './push-events';
import {EMPTY, Observable, Subject} from 'rxjs';
import {catchError, switchMap, takeUntil, tap, timeout} from 'rxjs/operators';
import {EventsResponse} from '@gt-api';
import {GtPushSubscriptionService} from './gt-push-subscription.service';
import {GtAuthenticationService} from '@app/services/gt-authentication.service';
import {checkResponse} from '@app/shared/utils/app-utils';

// Class containing observables that should notify services subscribing to the given push type.
// Names have to match the push event type in the message string
export class PushSubjects {
  Available: Subject<Available> = new Subject<Available>();
  Absent: Subject<Absent> = new Subject<Absent>();
  AgentStateTransition: Subject<AgentStateTransition> = new Subject<AgentStateTransition>();
  AnsweredCall: Subject<AnsweredCall> = new Subject<AnsweredCall>();
  CallInfo: Subject<CallInfo> = new Subject<CallInfo>();
  CallLog: Subject<CallLogEvent> = new Subject<CallLogEvent>();
  CallToggled: Subject<CallToggled> = new Subject<CallToggled>();
  CreateCall: Subject<CreateCall> = new Subject<CreateCall>();
  DisconnectedCall: Subject<DisconnectedCall> = new Subject<DisconnectedCall>();
  NoConnectCall = new Subject<NoConnectCall>();
  EUCreateCall: Subject<EUCreateCall> = new Subject<EUCreateCall>();
  EUAnsweredCall: Subject<EUAnsweredCall> = new Subject<EUAnsweredCall>();
  EUExpiredCall: Subject<EUExpiredCall> = new Subject<EUExpiredCall>();
  EUNoConnectCall: Subject<EUNoConnectCall> = new Subject<EUNoConnectCall>();
  EUDisconnectedCall: Subject<EUDisconnectedCall> = new Subject<EUDisconnectedCall>();
  MobilityManagementUpdate = new Subject<MobilityManagementUpdateEvent>();
  SkillAdded: Subject<SkillAdded> = new Subject<SkillAdded>();
  SkillChanged: Subject<SkillChanged> = new Subject<SkillChanged>();
  SkillRemoved: Subject<SkillRemoved> = new Subject<SkillRemoved>();
  TransferCallAnswered: Subject<TransferCallAnswered> = new Subject<TransferCallAnswered>();
  TransferCallEnd: Subject<TransferCallEnd> = new Subject<TransferCallEnd>();
  TransferCall: Subject<TransferCallEvent> = new Subject<TransferCallEvent>();
  Enqueue: Subject<Enqueue> = new Subject<Enqueue>();
  Dequeue: Subject<Dequeue> = new Subject<Dequeue>();
  CallRetrieved: Subject<CallRetrieved> = new Subject<CallRetrieved>();
}

@Injectable()
export class GtWebSocketService {
  private ws: WebSocket;
  private onopenSubject: Subject<void>;
  private onmessageSubject: Subject<MessageEvent>;
  private oncloseSubject: Subject<CloseEvent>;
  private onerrorSubject: Subject<ErrorEvent>;
  isRunning = true;

  get onopen(): Observable<void> {
    return this.onopenSubject.asObservable();
  }

  get onmessage(): Observable<MessageEvent> {
    return this.onmessageSubject.asObservable();
  }

  get onclose(): Observable<CloseEvent> {
    return this.oncloseSubject.asObservable();
  }

  get onerror(): Observable<ErrorEvent> {
    return this.onerrorSubject.asObservable();
  }

  newWebsocket(url: string): void {
    this.onopenSubject = new Subject<void>();
    this.onmessageSubject = new Subject<MessageEvent>();
    this.oncloseSubject = new Subject<CloseEvent>();
    this.onerrorSubject = new Subject<ErrorEvent>();
    this.ws = new WebSocket(url);

    this.connect();
  }

  closeWebSocket() {
    this.isRunning = false;
    if (this.ws) {
      this.ws.close();
      this.onopenSubject.complete();
      this.onmessageSubject.complete();
      this.oncloseSubject.complete();
      this.onerrorSubject.complete();
    }
  }

  private connect() {
    this.isRunning = true;
    this.ws.onopen = () => this.onopenSubject.next();
    this.ws.onmessage = event => this.onmessageSubject.next(event);
    this.ws.onclose = event => this.oncloseSubject.next(event);
    this.ws.onerror = event => this.onerrorSubject.next(event as any);
  }
}

@Injectable({
  providedIn: 'root'
})
export class PushEventService implements OnDestroy {
  private webSocketPath: string;

  pushSubjects: PushSubjects = new PushSubjects();
  private timeoutHandle: number;
  public lastPollSuccess: number;
  private timeHandler;
  private isSubscribingToPush = false;
  private destroyed = new Subject<void>();

  constructor(
    private authenticationSerivce: GtAuthenticationService,
    private pushSubscriptionService: GtPushSubscriptionService,
    private windowService: GtWindowService,
    private webSocketService: GtWebSocketService
  ) {
    if (environment.useWebsocket && !environment.wsUrl) {
      console.error('No wsUrl defined');
      return;
    }
    if (environment.useWebsocket || environment.useLongPolling) {
      this.webSocketPath = environment.wsUrl;
      this.authenticationSerivce
        .isloggedIn()
        .pipe(takeUntil(this.destroyed))
        .subscribe(loggedIn => {
          if (loggedIn) {
            this.subscribePush();
          } else if (!loggedIn && environment.useWebsocket && this.webSocketService) {
            this.webSocketService.closeWebSocket();
            this.isSubscribingToPush = false;
          }
        });
    } else {
      console.log('Eventing disabled');
    }
  }

  protected subscribePush(): void {
    if (this.isSubscribingToPush) {
      console.error('already subscribing');
      return;
    }
    this.isSubscribingToPush = true;
    this.pushSubscriptionService
      .subscribeEndUser1([])
      .pipe(
        switchMap(() => this.pushSubscriptionService.subscribeAbsence1([])),
        switchMap(() => this.pushSubscriptionService.subscribeVirtualNumber1([])),
        tap(() => {
          if (environment.useWebsocket) {
            this.connectWebSocket();
          } else if (environment.useLongPolling) {
            this.pollEvents();
          } else {
            console.log('Eventing disabled');
          }
        }),
        catchError(error => {
          console.error('Failed to set up subscriptions. No events will be received.', error);
          return EMPTY;
        })
      )
      .subscribe();
  }

  protected connectWebSocket(): void {
    console.log('Connecting to Web socket: ' + this.webSocketPath);
    this.timeoutHandle = null;
    this.webSocketService.newWebsocket(this.webSocketPath);

    this.webSocketService.onopen.subscribe(() => {
      console.log('Web Socket is connected');
    });

    this.webSocketService.onmessage.subscribe(evt => {
      const receivedMessage: string = evt.data;
      this.parseAndPublish(receivedMessage);
    });

    this.webSocketService.onclose.subscribe(evt => {
      console.log('Web Socket connection is closed...');
      if (evt.code !== 0) {
        console.log(`Cannot connect to server: code=${evt.code} reason=${evt.reason}`);
      }
      if (this.webSocketService.isRunning) {
        this.reconnectWebsocket();
      }
    });

    this.webSocketService.onerror.subscribe(evt => {
      console.log('Web Socket error: ', evt.error);
      this.reconnectWebsocket();
    });

    this.windowService.onbeforeunload = event => {
      this.webSocketService.closeWebSocket();
    };
  }

  protected reconnectWebsocket() {
    if (!this.timeoutHandle) {
      this.timeoutHandle = this.windowService.setTimeout(() => this.connectWebSocket(), 5000);
      console.log('Reconnecting websocket: ' + this.timeoutHandle);
    } else {
      console.log('Reconnecting websocket already started: ' + this.timeoutHandle);
    }
  }

  pollEvents() {
    if (new Date().getTime() - this.lastPollSuccess > environment.maxTimeSinceLastPollSuccess) {
      console.log('Looks like we might have lost some events. Performing a refresh.');
      this.windowService.reload();
      return;
    }
    this.pushSubscriptionService
      .getEvents1()
      .pipe(
        timeout(environment.pollingTimeout),
        checkResponse(),
        this.checkEventStatus(),
        tap(response => {
          this.lastPollSuccess = new Date().getTime();
          if (response.events) {
            this.publishEvents(response.events);
          }
        })
      )
      .subscribe(
        () => this.pollEvents(),
        error => {
          console.log('getEvents() error: ', error.message);
          this.releaseSubscription();
          if (error.status === 401) {
            this.authenticationSerivce.doLogout().subscribe();
          } else if (error.message === 'suspect') {
            this.authenticationSerivce.doLogout().subscribe();
          } else if (error.message === 'invalid') {
            this.windowService.reload();
          } else {
            this.timeHandler = this.windowService.setTimeout(() => this.pollEvents(), 10000);
          }
        }
      );
  }

  private checkEventStatus() {
    return tap((response: EventsResponse) => {
      if (response.suspect) {
        throw Error('suspect');
      }
      if (response.invalid) {
        throw Error('invalid');
      }
    });
  }

  parseAndPublish(pushMessageString: string) {
    const pushMessageObject = JSON.parse(pushMessageString);
    if (pushMessageObject.invalid) {
      this.windowService.reload();
      return;
    }
    if (pushMessageObject.suspicious) {
      console.log('Received a suspicious push message: ', pushMessageObject);
      this.authenticationSerivce.doLogout().subscribe();
      return;
    }
    this.publishEvents(pushMessageObject.events);
  }

  publishEvents(eventsMap: any) {
    // Look for events of the types specified in this.pushSubjects
    /*
    {
      "invalid": "false",
      "suspicious": "false",
      "EUAnsweredCall": [
      {
        "session_id": 602594019,
        "dep_id": 0,
        "full_number": "4791902156",
        ...
        "timestamp": 1571049996596
      },
      {
        "session_id": 402594018,
        "dep_id": 0,
        "full_number": "4791902156",
        ...
        "timestamp": 1571049996907
      }
      ],
      "AnsweredCall": {
        "session_id": 402594018,
        "sno": "4770238818",
        "cid": 0,
        ...
        "timestamp": 1571049996919
      }
    }
    */

    let eventList = [];
    // convert map to list, adding property 'pushEventType' to elements
    Object.keys(eventsMap).forEach(pushEventType => {
      if (this.pushSubjects[pushEventType]) {
        const events = [].concat(eventsMap[pushEventType]);
        events.forEach(element => {
          element.pushEventType = pushEventType;
        });
        eventList = eventList.concat(events);
      }
    });

    // sort list according to timestamp to maintain order
    eventList.sort((a, b) => {
      return a.timestamp - b.timestamp;
    });

    // using 'pushEventType' to select pushSubject to send message on
    eventList.forEach(pushEvent => {
      const pushEventType = pushEvent.pushEventType;
      const pushSubject = this.pushSubjects[pushEventType];
      if (pushSubject !== null) {
        console.log(`>>>>>>>>>>> ${pushEventType}`);
        console.log(JSON.stringify(pushEvent, null, 2));
        pushSubject.next(pushEvent);
      }
    });
  }

  private releaseSubscription() {
    if (this.timeHandler) {
      clearTimeout(this.timeHandler);
    }
  }

  ngOnDestroy() {
    this.destroyed.next();
    this.destroyed.complete();
  }
}
